import { useMemo, useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
    Box,
    CircularProgress,
    Paper
} from '@mui/material';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import {
    DataGridPro,
    GridToolbar,
    GridColDef,
    GridRenderCellParams,
    GridRowId
} from '@mui/x-data-grid-pro';
import { toast } from 'react-toastify';
import axios from 'axios';
import dayjs from 'dayjs';
import { socket } from '../services/socket';
import { formatCurrency } from '../utils/utils';

const api = axios.create({
    withCredentials: true
});

interface ApprovalInfo {
    approvalByAuth0Id?: string;
    approvalDate?: string;
}

interface LineItem {
    lineItemId: string | number;
    invoiceSourceId?: string | number;
    eventSourceId?: string | number;
    payOnDate?: string;
    lastPaymentDate?: string;
    revenueSplit?: number;
    profitSplit?: number;
    pastSplit?: number;
    totalSplit?: number;
    clawbackAmount?: number;
    clawbackReason?: string;
    shippingTrackingStatus?: string;
    invoiceTicketCost?: number;
    vendorId?: string | number;
    vendorSourceId?: string | number;
}

interface InvoiceLineItem {
    lineItem?: LineItem;
    approvalInfo?: ApprovalInfo;
    brokerCompanyName?: string;
}

interface BrokerInvoiceGridProps {
    lineItems: InvoiceLineItem[];
}

interface BrokerDetailRow {
    id: string | number;
    brokerId: string | number;
    brokerName?: string;

    lineItems: InvoiceLineItem[];

    approvedCount: number;
    totalCount: number;
    approvedCommission: number;
}

const BrokerInvoiceGrid = ({lineItems}: BrokerInvoiceGridProps) => {
    const rows = lineItems.map((invoice) => {
        const approval = invoice.approvalInfo;

        const isApproved = Boolean(
            approval?.approvalByAuth0Id &&
            approval?.approvalDate
        );

        const lineItem = invoice.lineItem!;

        return {
            id: lineItem.lineItemId,
            lineItemId: lineItem.lineItemId,
            status: isApproved
                ? 'APPROVED'
                : 'OPEN',
            tnInvoiceId: lineItem.invoiceSourceId,
            eventId: lineItem.eventSourceId,
            payDateWithinCycle:
                dayjs(lineItem.payOnDate)
                    .format('MM/DD/YYYY hh:mm A'),
            revenueSplit: lineItem.revenueSplit,
            profitSplit: lineItem.profitSplit,
            pastSplit: lineItem.pastSplit,
            totalSplit: lineItem.totalSplit,
            clawbackAmount: lineItem.clawbackAmount,
            clawbackReason: lineItem.clawbackReason,
            commissionPayedOn:
                dayjs(lineItem.lastPaymentDate)
                    .format('MM/DD/YYYY hh:mm A'),
            shippingTrackingStatus:
                lineItem.shippingTrackingStatus,
            ticketCost:
                lineItem.invoiceTicketCost,
        };
    });

    const columns: GridColDef[] = [
        {
            field: 'status',
            headerName: 'Status',
            flex: 0.15,
            renderCell: (
                params: GridRenderCellParams
            ) => {
                const isApproved =
                    params.value === 'APPROVED';

                return (
                    <span
                        style={{
                            color: isApproved
                                ? '#2e7d32'
                                : '#ed6c02',
                            fontWeight: 700
                        }}
                    >
                        {params.value}
                    </span>
                );
            }
        },
        {
            field: 'tnInvoiceId',
            headerName: 'Invoice ID',
            flex: 0.2,
            minWidth: 80
        },
        {
            field: 'eventId',
            headerName: 'Event ID',
            flex: 0.2,
            minWidth: 80
        },
        {
            field: 'payDateWithinCycle',
            headerName: 'Pay Date',
            flex: 0.2,
            minWidth: 120
        },
        {
            field: 'ticketCost',
            headerName: 'Ticket Cost',
            flex: 0.15,
            renderCell: (params: GridRenderCellParams) => formatCurrency(params.value)
        },
        {
            field: 'revenueSplit',
            headerName: 'Revenue Split',
            flex: 0.15,
            renderCell: (params: GridRenderCellParams) => formatCurrency(params.value)
        },
        {
            field: 'profitSplit',
            headerName: 'Profit Split',
            flex: 0.15,
            renderCell: (params: GridRenderCellParams) => formatCurrency(params.value)
        },
        {
            field: 'pastSplit',
            headerName: 'Past Split',
            flex: 0.15,
            renderCell: (params: GridRenderCellParams) => formatCurrency(params.value)
        },
        {
            field: 'clawbackAmount',
            headerName: 'Clawback',
            flex: 0.15,
            renderCell: (params: GridRenderCellParams) => formatCurrency(params.value)
        },
        {
            field: 'clawbackReason',
            headerName: 'Clawback Reason',
            flex: 0.15,
        },
        {
            field: 'totalSplit',
            headerName: 'Payout',
            flex: 0.15,
            renderCell: (params: GridRenderCellParams) => formatCurrency(params.value)
        },
        {
            field: 'commissionPayedOn',
            headerName: 'Paid On',
            flex: 0.2
        },
        {
            field: 'shippingTrackingStatus',
            headerName: 'Shipping Status',
            flex: 0.2
        }
    ];

    return (
        <DataGridPro
            rows={rows}
            columns={columns}
            hideFooter
            disableRowSelectionOnClick
            initialState={{
                sorting: {
                    sortModel: [
                        {
                            field: 'status',
                            sort: 'asc'
                        }
                    ]
                }
            }}
        />
    );
};

interface InvoiceDetailsGridProps {
    row: {
        invoiceLineItems?: InvoiceLineItem[];
    };
}

const InvoiceDetailsGrid = ({row}: InvoiceDetailsGridProps) => {
    const groupedByBroker = useMemo<BrokerDetailRow[]>(() => {
        const map = new Map<
            string | number,
            BrokerDetailRow
        >();

        (row.invoiceLineItems || [])
            .forEach((item) => {
                const li = item?.lineItem;
                const approval = item?.approvalInfo;

                if (!li) {
                    return;
                }

                const brokerName = item?.brokerCompanyName;;
                const brokerId = li.vendorId ?? '';
                const tnBrokerId = li.vendorSourceId ?? '';
                const commissionValue = Number(li.totalSplit || 0);

                const isApproved = Boolean(
                    approval?.approvalByAuth0Id &&
                    approval?.approvalDate
                );

                if (!map.has(brokerId)) {
                    map.set(
                        brokerId,
                        {
                            id: brokerId,
                            brokerId: tnBrokerId,
                            brokerName,
                            lineItems: [],
                            approvedCount: 0,
                            totalCount: 0,
                            approvedCommission: 0
                        }
                    );
                }

                const broker =
                    map.get(brokerId)!;

                broker.lineItems.push(item);
                broker.totalCount += 1;

                if (isApproved) {
                    broker.approvedCount += 1;
                    broker.approvedCommission += commissionValue;
                }
            });

        return Array.from(
            map.values()
        );

    }, [row.invoiceLineItems]);

    const brokerColumns: GridColDef[] = [
        {
            field: 'brokerId',
            headerName: 'Broker ID',
            flex: 0.1
        },
        {
            field: 'brokerName',
            headerName: 'Broker Name',
            flex: 0.6
        },
        {
            field: 'approved',
            headerName: 'Approved',
            flex: 0.2,
            valueGetter: (
                _value,
                row: BrokerDetailRow
            ) =>
                `${row.approvedCount}/${row.totalCount}`
        },
        {
            field: 'approvedCommission',
            headerName: 'Approved Payout',
            flex: 0.2,
            renderCell: (
                params: GridRenderCellParams
            ) =>
                formatCurrency(
                    params.value || 0
                )
        }
    ];

    const getDetailPanelContent = useCallback(
        ({
            row
        }: {
            row: BrokerDetailRow;
        }) => (
            <BrokerInvoiceGrid
                lineItems={
                    row.lineItems
                }
            />

        ), []
    );

    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Box
                sx={{
                    height: 320
                }}
            >
                <DataGridPro
                    rows={groupedByBroker}
                    getRowId={(row: BrokerDetailRow) => row.brokerId}
                    columns={brokerColumns}
                    hideFooter
                    disableRowSelectionOnClick
                    getDetailPanelHeight={() => 280}
                    getDetailPanelContent={getDetailPanelContent}
                    density="compact"
                />
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    borderTop: '1px solid #e0e0e0',
                    pt: 1,
                    pr: 1
                }}
            >
            </Box>
        </Box>
    );
};

interface MaestroOutletContext {
    maestroPermissions?: string[];
    isConnected: boolean;
}

interface Cycle {
    cycleId: string | number;
    startDate: string;
    endDate: string;
}

interface VendorLineItems {
    vendor?: {
        companyName?: string;
    };

    lineItems?: InvoiceLineItem[];
}

interface ClosedCycleResponse {
    cycle: Cycle;
    vendorsAndLineItems: VendorLineItems[];
}

interface ClosedCycleData {
    cycle: Cycle;
    vendors: VendorLineItems[];
}

interface CycleGridRow {
    id: string | number;
    cycleId: string | number;
    commissionStartDate: string;
    commissionEndDate: string;
    approvedPayout: number;
    vendorsAndLineItems: VendorLineItems[];
}

function CloseCyclesReport() {
    const {
        maestroPermissions,
        isConnected
    } = useOutletContext<MaestroOutletContext>();

    const {
        getAccessTokenSilently
    } = useAuth0();

    const [columns, setColumns] = useState<GridColDef[] | null>(null);
    const [rows, setRows] = useState<CycleGridRow[] | null>(null);
    const [data, setData] = useState<ClosedCycleData[]>([]);
    const [expandedRowIds, setExpandedRowIds] = useState<GridRowId[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const isAuthorized =
        maestroPermissions?.includes(
            `${process.env.REACT_APP_TENANT}:Commissions:Cycles:Read`
        );

    const getClosedCyclesReport =
        useCallback(async () => {
            try {

                setIsLoading(true);

                const token =
                    await getAccessTokenSilently({
                        authorizationParams: {
                            audience:
                                process.env
                                    .REACT_APP_AUTH0AUDIENCE_MAESTRO
                        }
                    });



                const response =
                    await api.get<ClosedCycleResponse[]>(
                        `${process.env.REACT_APP_MAESTRO_DOMAIN}/commissions/cycles/closed/lineitems/`,
                        {
                            headers: {
                                Authorization: `Bearer ${token}`,
                                'x-tenant': process.env.REACT_APP_TENANT
                            }
                        }
                    );

                setData(
                    response.data.map(
                        (item) => ({
                            cycle: item.cycle,
                            vendors: item.vendorsAndLineItems
                        })
                    )
                );
            } catch (err: unknown) {
                if ( axios.isAxiosError(err)) {
                    toast.error(
                        err.response?.data?.message ||
                        'Request failed'
                    );
                } else {
                    toast.error(
                        'Request failed'
                    );
                }
            } finally {
                setIsLoading(false);
            }
        }, [ getAccessTokenSilently ]);

    const createRowsAndColumns =
        useCallback(() => {
            const rows: CycleGridRow[] =
                data.map((item) => {
                    const approvedPayout =
                        item.vendors?.reduce(( vendorTotal, vendor ) => {
                            const approvedVendorTotal = ( vendor.lineItems ?? [] )
                                .reduce(( lineTotal, lineItem ) => {
                                    const isApproved =
                                        Boolean(
                                            lineItem
                                                ?.approvalInfo
                                                ?.approvalByAuth0Id &&
                                            lineItem
                                                ?.approvalInfo
                                                ?.approvalDate
                                        );
                                            return (
                                                lineTotal +
                                                (
                                                    isApproved
                                                        ? Number(
                                                            lineItem
                                                                ?.lineItem
                                                                ?.totalSplit || 0
                                                        )
                                                        : 0
                                                )

                                            );

                                        },

                                        0

                                    );

                                return (
                                    vendorTotal +
                                    approvedVendorTotal
                                );
                            },
                            0
                        ) ?? 0;

                    return {
                        id: item.cycle.cycleId,
                        cycleId: item.cycle.cycleId,
                        commissionStartDate: dayjs(item.cycle.startDate).format('MM/DD/YYYY hh:mm A'),
                        commissionEndDate: dayjs(item.cycle.endDate).format('MM/DD/YYYY hh:mm A'),
                        approvedPayout,
                        vendorsAndLineItems: item.vendors
                    };
                });

            const columns: GridColDef[] = [
                {
                    field: 'cycleId',
                    headerName: 'Cycle ID',
                    flex: 0.05
                },
                {
                    field: 'commissionStartDate',
                    headerName: 'Period Start',
                    flex: 0.1
                },
                {
                    field: 'commissionEndDate',
                    headerName: 'Period End',
                    flex: 0.1
                },
                {
                    field: 'approvedPayout',
                    headerName: 'Approved Payout',
                    flex: 0.15,
                    renderCell: (
                        params: GridRenderCellParams
                    ) =>
                        formatCurrency(
                            params.value || 0
                        )
                }

            ];

            setRows(rows);
            setColumns(columns);

        }, [data]);

    const getDetailPanelContent =
        ({
            row
        }: {
            row: CycleGridRow;
        }) => (
            <InvoiceDetailsGrid
                row={{
                    invoiceLineItems:
                        row
                            ?.vendorsAndLineItems
                            ?.flatMap(
                                (vendor) =>
                                    (vendor.lineItems ?? [])
                                    .map(
                                        (li) => ({
                                            ...li,
                                            brokerCompanyName:
                                                vendor.vendor
                                                    ?.companyName
                                        })
                                    )
                            ) ?? []
                }}
            />
        );

    const getDetailPanelHeight =
        useCallback(
            () => 377, []
        );

    useEffect(() => {
        if ( !isConnected || !isAuthorized ) {
            return;
        }

        getClosedCyclesReport();

        socket.on('closedCyclesReport', getClosedCyclesReport);

        return () => {
            socket.off('closedCyclesReport', getClosedCyclesReport);
        };
    }, [ isConnected, isAuthorized, getClosedCyclesReport ]);

    useEffect(() => {
        if (data.length > 0) {
            createRowsAndColumns();
        } else {
            setRows([]);
            setColumns([]);
        }
    }, [ data, createRowsAndColumns ]);


    if (!isAuthorized) {
        return (
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    alignItems: 'center',
                    textAlign: 'center'
                }}
            >
                <WarningRoundedIcon
                    fontSize="large"
                />
                <p>
                    Not Authorized
                </p>
            </Box>
        );
    }

    if (!rows || !columns) {
        return (
            <Box
                sx={{
                    p: 3,

                    textAlign: 'center'
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Paper>
            <Box
                sx={{
                    height: '85vh',
                    width: '100%'
                }}
            >
                <DataGridPro
                    slots={{
                        toolbar:
                            GridToolbar
                    }}
                    slotProps={{
                        toolbar: {
                            showQuickFilter: true,
                            quickFilterProps: {
                                debounceMs: 500
                            },
                            csvOptions: {
                                disableToolbarButton: true,
                            },
                            printOptions: {
                                disableToolbarButton: true,
                            },
                        }
                    }}
                    loading={isLoading}
                    rows={rows}
                    columns={columns}
                    pagination
                    disableRowSelectionOnClick
                    getDetailPanelContent={getDetailPanelContent}
                    getDetailPanelHeight={getDetailPanelHeight}
                    detailPanelExpandedRowIds={expandedRowIds}
                    onDetailPanelExpandedRowIdsChange={setExpandedRowIds}
                />
            </Box>
        </Paper>
    );
}

export default CloseCyclesReport;